import styles from './DealApplicationDrawer.module.scss';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import React from 'react';
import useFetchConfigForDrawer from './customHooks/useFetchConfigForDrawer';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import useDealApplicationDrawerLogic from './customHooks/useDealApplicationDrawerLogic';
import { ManageDealDrawerHeader } from '../ManageDealDrawer/Components/ManageDealDrawerHeader';
import FormPage from './Components/Page/FormPage';
import FormSideStepper from './Components/FormSideStepper/FormSideStepper';
import useFetchFormData from './customHooks/useFetchFormData';
import SideDrawer from '../_custom/SideDrawer/SideDrawer';
import { ManageDealDrawerFooter } from '../ManageDealDrawer/Components/ManageDealDrawerFooter/ManageDealDrawerFooter';
import DealSuccessModal from './Components/DealSuccessModal/DealSuccessModal';
import ConfirmationModal from '../../modules/Company/UploadJourney/components/ProvisionalFinancials/components/DataMappingDrawer/ConfirmationModal/ConfirmationModal';
import { DealFilterRenderer } from '../DealFiltersDrawer/DealFilterRenderer';
import { checkIfAnyFilterSelected } from '../../utils/utils';
import { USER_TYPE } from '../ManageDealDrawer/DealDrawerConstants';
import { useNavigate, useParams } from 'react-router-dom';
import { updateQueryParams } from '../../utils/searchParamUtils';

const DealApplicationDrawer = () => {
    const drawerState = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const params = useParams();
    const userData = useAppSelector((state) => state.user.userData);
    const { drawerConfig, loadingConfig } = useFetchConfigForDrawer(drawerState.open);
    const showSuccessModal = useAppSelector((state) => state.investor.dealApplicationDrawer.showSuccessModal);
    const isCompanyDealsPage = location.pathname.includes('company-deals');
    const isCompanySide = userData.accountType === USER_TYPE.ASSESSEE;
    const assesseeOrgId = params.id;
    const navigate = useNavigate();

    const {
        updateDealTerms,
        formData,
        handlePrimaryClick,
        handleBack,
        step,
        setStep,
        updateLenderMap,
        loading,
        isFormValid,
        setIsFormValid,
        showConfirmation,
        setShowConfirmation,
        tempFilters,
        setTempFilters,
        searchParams,
        setSearchParams,
        getTransformedData,
    } = useFetchFormData(drawerState.open, drawerConfig.disabledFields);
    const { closeDrawer, optionsMap } = useDealApplicationDrawerLogic(setTempFilters);
    const getIsExistingLenders = () => {
        // Iterate over the formData.dealReceiverDetailMap and check if all of the lenders are existing
        let isExistingLenders = true;
        formData?.dealReceiverDetailMap?.forEach((value) => {
            if (!value.isExisting) {
                isExistingLenders = false;
            }
        });
        return isExistingLenders;
    };

    const disablePrimary = () => {
        const source = drawerState.source;
        if (source === 'DEAL_FILTERS') {
            return false;
        }
        return (
            loading ||
            !isFormValid ||
            (step === 1 && formData?.dealReceiverDetailMap?.size === 0) ||
            (step === 1 && getIsExistingLenders() && formData?.mandateState !== 'DRAFT')
        );
    };
    const disableSecondary = () => {
        const source = drawerState.source;
        if (source === 'DEAL_FILTERS') {
            return !checkIfAnyFilterSelected(tempFilters);
        }
        return loading || !isFormValid;
    };

    return (
        <>
            {drawerState.open && (
                <SideDrawer width={drawerState.width} onClose={closeDrawer}>
                    <div
                        style={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div className={styles.DrawerHeader}>
                            <ManageDealDrawerHeader
                                drawerLabel={drawerState.heading}
                                investorName={drawerState.subHeading}
                                noBorder={true}
                            />
                        </div>

                        <div className={styles.DrawerBody}>
                            {loadingConfig ? (
                                <LoadingSpinner color={'#6021b3'} height={'3rem'} />
                            ) : (
                                <>
                                    {drawerConfig?.stepper?.length > 0 && (
                                        <FormSideStepper
                                            isFormValid={isFormValid}
                                            onStepChange={setStep}
                                            stepperConfig={drawerConfig.stepper}
                                            step={step}
                                        />
                                    )}
                                    {drawerConfig?.pages?.length > 0 && (
                                        <div className={styles.DrawerContent}>
                                            <div className={styles.PagesWrapper}>
                                                {drawerConfig?.pages.map((page, index) => {
                                                    // Decide which class to apply:
                                                    let pageClass = styles.pageBelow;
                                                    if (index === step) {
                                                        pageClass = styles.pageCurrent;
                                                    } else if (index < step) {
                                                        pageClass = styles.pageAbove;
                                                    }

                                                    return (
                                                        <div key={index} className={clsx(styles.PageContainer, pageClass)}>
                                                            {page.text && (
                                                                <div className={styles.PageTitle}>
                                                                    {index + 1}
                                                                    {'. '}
                                                                    {page.text}
                                                                </div>
                                                            )}
                                                            {page.subText && <div className={styles.PageDescription}>{page.subText}</div>}
                                                            <FormPage
                                                                step={step}
                                                                setIsFormValid={setIsFormValid}
                                                                page={page.config}
                                                                formData={formData}
                                                                updateDealTerms={updateDealTerms}
                                                                updateLenderMap={updateLenderMap}
                                                                getTransformedData={getTransformedData}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {drawerConfig?.filters?.pages?.length > 0 && (
                                        <div className={styles.DrawerContent}>
                                            <div className={styles.PagesWrapperFilters}>
                                                {drawerConfig?.filters?.pages.map((page, index) => {
                                                    return (
                                                        <DealFilterRenderer
                                                            key={index}
                                                            options={optionsMap[page.step]}
                                                            isApplied={tempFilters[page.step]?.length > 0}
                                                            config={page}
                                                            tempFilters={tempFilters}
                                                            setTempFilters={setTempFilters}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <ManageDealDrawerFooter
                            currentConfig={drawerConfig?.pages[step] || drawerConfig?.filters}
                            disablePrimary={disablePrimary()}
                            onPrimary={async () => handlePrimaryClick(false)}
                            onSecondary={handleBack}
                            style={{ zIndex: '1' }}
                            disableSecondary={disableSecondary()}
                        />
                    </div>
                </SideDrawer>
            )}
            <DealSuccessModal
                showSuccessModal={showSuccessModal}
                buttonOnClick={() => {
                    if (isCompanySide) {
                        return;
                    } else if (isCompanyDealsPage) {
                        updateQueryParams(searchParams, setSearchParams, { refetchCompanyDeals: true });
                    } else if (assesseeOrgId) {
                        navigate(
                            `/investor/app/company-deals/${assesseeOrgId}?page=1&fetchType=all&type=deals&category=DEAL_VIEW&tab=deals&dealUpdated=true`,
                            {
                                state: { fromDealSuccess: true },
                            },
                        );
                    } else {
                        navigate('/investor/app/deals?page=1&type=deals&dealUpdated=true', {
                            state: { fromDealSuccess: true },
                        });
                    }
                }}
            />
            <ConfirmationModal
                heading={'Save Changes?'}
                subHeading={'Current changes will not apply to the already modified lender terms'}
                saveCTAText={'Confirm'}
                noSaveCTAText={'Cancel'}
                showModal={{
                    closeModal: showConfirmation,
                }}
                onClose={() => {
                    setShowConfirmation(false);
                }}
                onClickSave={() => {
                    if (step === 0) {
                        handlePrimaryClick(true);
                        setShowConfirmation(false);
                    } else {
                        setShowConfirmation(false);
                    }
                }}
                onClickNoSave={() => setShowConfirmation(false)}
                isInvestor={true}
            />
        </>
    );
};

export default DealApplicationDrawer;
